/**
 * Date/Time Formatting Utilities - Eastern Time (America/New_York)
 * 
 * All owner portal dates/times must render in Florida property timezone:
 * America/New_York (Eastern Time with DST)
 * 
 * This ensures consistent display regardless of viewer's browser timezone.
 */

export const ET_TZ = 'America/New_York';

/**
 * Format date-only string (YYYY-MM-DD) as ET calendar date
 * 
 * Handles date-only strings without timezone shifts by constructing
 * a UTC date at noon, then formatting in ET timezone.
 * 
 * @param {string} yyyyMmDd - Date string in YYYY-MM-DD format
 * @param {Object} options - Formatting options
 *   - long: boolean - Use long format (e.g., "April 1, 2026")
 *   - short: boolean - Use short format (e.g., "Apr 1, 2026")
 *   - monthDay: boolean - Month and day only (e.g., "Apr 1")
 * @returns {string} - Formatted date string in ET
 */
export function formatDateOnlyET(yyyyMmDd, options = {}) {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string') {
    return 'N/A';
  }
  
  // Parse YYYY-MM-DD format manually to avoid timezone shifts
  const dateMatch = yyyyMmDd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    return 'N/A';
  }
  
  const [, yearStr, monthStr, dayStr] = dateMatch;
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10) - 1; // JavaScript months are 0-indexed
  const day = parseInt(dayStr, 10);
  
  // Validate
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return 'N/A';
  }
  
  // Create UTC date at noon to avoid boundary issues
  // Then format in ET timezone (this ensures correct calendar date)
  const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0));
  
  // Determine format options
  const formatOptions = {};
  
  if (options.monthDay) {
    formatOptions.month = 'short';
    formatOptions.day = 'numeric';
  } else if (options.short) {
    formatOptions.year = 'numeric';
    formatOptions.month = 'short';
    formatOptions.day = 'numeric';
  } else {
    // Default: long format
    formatOptions.year = 'numeric';
    formatOptions.month = 'long';
    formatOptions.day = 'numeric';
  }
  
  // Format in ET timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    ...formatOptions,
    timeZone: ET_TZ,
  });
  
  return formatter.format(utcDate);
}

/**
 * Format ISO timestamp as ET date/time
 * 
 * @param {string|Date} isoString - ISO timestamp string or Date object
 * @param {Object} options - Formatting options
 *   - dateOnly: boolean - Show date only (no time)
 *   - timeOnly: boolean - Show time only (no date)
 *   - long: boolean - Use long date format
 *   - short: boolean - Use short date format
 * @returns {string} - Formatted date/time string in ET
 */
export function formatDateTimeET(isoString, options = {}) {
  if (!isoString) {
    return 'N/A';
  }
  
  // Parse ISO string or use Date object
  const date = typeof isoString === 'string' ? new Date(isoString) : isoString;
  
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return 'N/A';
  }
  
  // Determine format options
  const formatOptions = {
    timeZone: ET_TZ,
  };
  
  if (options.timeOnly) {
    formatOptions.hour = 'numeric';
    formatOptions.minute = '2-digit';
    formatOptions.hour12 = true;
  } else if (options.dateOnly) {
    if (options.short) {
      formatOptions.year = 'numeric';
      formatOptions.month = 'short';
      formatOptions.day = 'numeric';
    } else {
      formatOptions.year = 'numeric';
      formatOptions.month = 'long';
      formatOptions.day = 'numeric';
    }
  } else {
    // Date and time
    formatOptions.year = 'numeric';
    formatOptions.month = options.short ? 'short' : 'long';
    formatOptions.day = 'numeric';
    formatOptions.hour = 'numeric';
    formatOptions.minute = '2-digit';
    formatOptions.hour12 = true;
  }
  
  const formatter = new Intl.DateTimeFormat('en-US', formatOptions);
  return formatter.format(date);
}

/**
 * Format date range (check-in to check-out) in ET
 * 
 * Normalizes both dates to YYYY-MM-DD first (property-local dates),
 * then formats them in ET timezone for display.
 * 
 * @param {string|Date|null|undefined} checkinValue - Check-in date (can be ISO datetime or date-only)
 * @param {string|Date|null|undefined} checkoutValue - Check-out date (can be ISO datetime or date-only)
 * @param {Object} options - Formatting options
 *   - separator: string - Separator between dates (default: " → ")
 *   - short: boolean - Use short format
 * @returns {string} - Formatted date range (e.g., "Apr 1, 2026 → Apr 5, 2026")
 */
export function formatDateRangeET(checkinValue, checkoutValue, options = {}) {
  // Normalize to YYYY-MM-DD first (property-local dates)
  const checkinYmd = normalizeToYmd(checkinValue);
  const checkoutYmd = normalizeToYmd(checkoutValue);
  
  if (!checkinYmd || !checkoutYmd) {
    return 'N/A';
  }
  
  const separator = options.separator || ' → ';
  const formatOptions = options.short ? { short: true } : {};
  
  const checkinFormatted = formatDateOnlyET(checkinYmd, formatOptions);
  const checkoutFormatted = formatDateOnlyET(checkoutYmd, formatOptions);
  
  if (checkinFormatted === 'N/A' || checkoutFormatted === 'N/A') {
    return 'N/A';
  }
  
  return `${checkinFormatted}${separator}${checkoutFormatted}`;
}

/**
 * Get current ET date/time for display
 * 
 * @returns {Date} - Current date/time in ET
 */
export function getCurrentETDate() {
  // Get current time in ET
  const now = new Date();
  const etFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  
  const parts = etFormatter.formatToParts(now);
  const year = parseInt(parts.find(p => p.type === 'year').value);
  const month = parseInt(parts.find(p => p.type === 'month').value) - 1;
  const day = parseInt(parts.find(p => p.type === 'day').value);
  const hour = parseInt(parts.find(p => p.type === 'hour').value);
  const minute = parseInt(parts.find(p => p.type === 'minute').value);
  const second = parseInt(parts.find(p => p.type === 'second').value);
  
  // Create date object (this will be in local time, but represents ET time)
  return new Date(year, month, day, hour, minute, second);
}

/**
 * Validate date-only string format (YYYY-MM-DD)
 * 
 * @param {string} dateString - Date string to validate
 * @returns {boolean} - True if valid YYYY-MM-DD format
 */
export function isDateOnly(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
}

/**
 * Normalize a date value to YYYY-MM-DD format (property-local date).
 * 
 * This is critical for check-in/check-out dates which must be treated as
 * property-local calendar dates, not timestamps subject to timezone conversion.
 * 
 * Handles:
 * - Date-only strings (YYYY-MM-DD) → returns as-is
 * - ISO datetime strings (YYYY-MM-DDTHH:mm:ssZ) → extracts YYYY-MM-DD portion
 * - Date objects → converts to YYYY-MM-DD in ET without shifting semantics
 * 
 * @param {string|Date|null|undefined} value - Date value to normalize
 * @returns {string|null} - YYYY-MM-DD format or null if invalid
 */
export function normalizeToYmd(value) {
  if (!value) return null;
  
  // If already YYYY-MM-DD format, return as-is
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  
  // If ISO datetime string (contains 'T'), extract YYYY-MM-DD portion
  if (typeof value === 'string' && value.includes('T')) {
    const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      return dateMatch[1]; // Return YYYY-MM-DD portion only
    }
  }
  
  // If Date object, format as YYYY-MM-DD in ET timezone
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    
    // Format in ET timezone to get the calendar date
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: ET_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    
    const parts = formatter.formatToParts(value);
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    
    if (year && month && day) {
      return `${year}-${month}-${day}`;
    }
  }
  
  // Try to parse as date string and extract YYYY-MM-DD
  if (typeof value === 'string') {
    const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      return dateMatch[1];
    }
  }
  
  return null;
}

/**
 * Format check-in/check-out date in ET timezone.
 * 
 * Normalizes the value to YYYY-MM-DD first (property-local date),
 * then formats it in ET timezone for display.
 * 
 * @param {string|Date|null|undefined} value - Date value (can be ISO datetime or date-only)
 * @param {Object} options - Formatting options (same as formatDateOnlyET)
 * @returns {string} - Formatted date string in ET, or 'N/A'
 */
export function formatCheckinCheckoutDateET(value, options = {}) {
  const ymd = normalizeToYmd(value);
  if (!ymd) return 'N/A';
  return formatDateOnlyET(ymd, options);
}
